import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface ModuleSettingsResponse {
  seasonal_sales_override: boolean;
}

const ModuleSettingsPage: React.FC = () => {
  const { data: response, isLoading, error } = useGet<ModuleSettingsResponse>({
    url: '/home_services/module_settings',
  });
  const { mutateAsync, isPending } = usePut();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [seasonalSalesOverride, setSeasonalSalesOverride] = useState(true);

  useEffect(() => {
    const data = response?.data;
    if (data) {
      setSeasonalSalesOverride(data.seasonal_sales_override);
    }
  }, [response]);

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/module_settings',
        data: { module_settings: { seasonal_sales_override: seasonalSalesOverride } },
      });
      showSuccessSnackBar?.('Settings saved successfully');
    } catch {
      showErrorSnackBar?.('Failed to save settings');
    }
  };

  if (error) return <ErrorMessage message={error.message} />;

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageHeader mainTitle="Module Settings" subTitle="Configure Home Services module behavior" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scheduled Jobs
            </Typography>
            <FormControlLabel
              control={
                <Switch checked={seasonalSalesOverride} onChange={(e) => setSeasonalSalesOverride(e.target.checked)} />
              }
              label="Seasonal Sales Override"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
              When enabled, sales personnel are included in the daily scheduled jobs import even when their activity is
              low.
            </Typography>
            <Button variant="contained" onClick={handleSave} disabled={isPending} sx={{ mt: 3 }}>
              {isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default ModuleSettingsPage;
